/*
 * dd-cart-state.js — one owner of the cart, for every script that needs it.
 *
 * WHY THIS EXISTS
 * Five scripts independently wanted to know what was in the cart, and each
 * solved it the same way: patch window.fetch, re-read /cart.js on every
 * mutation, then poll /cart.js every 1.2s for 45s afterwards. Measured on the
 * preview theme (01 Sep 2026), ONE add to cart produced 33 requests, 25 of them
 * /cart.js, in synchronised bursts. Browsers allow ~6 connections per host, so
 * a second add queues behind that storm — which is how a 2.1s add is
 * experienced as twenty seconds.
 *
 * WHAT IT DOES
 * One fetch/XHR patch, one poll, one in-flight request, one short cache. Every
 * consumer subscribes instead of fetching. An add now costs one read plus one
 * settle read, not twenty-five.
 *
 * API (all optional to callers — every consumer keeps a fallback path, so a
 * load-order accident degrades to the old behaviour rather than breaking):
 *   ddCart.get()            -> Promise<cart>   cached + de-duped
 *   ddCart.subscribe(fn)    -> unsubscribe     fn(cart) on every fresh read
 *   ddCart.refresh(now)     -> void            debounced re-read + notify
 *   ddCart.poke()           -> void            cart panel opened: short backoff watch
 *
 * Load this BEFORE any consumer (it sits above the gokwik/collection-limit
 * renders in layout/theme.liquid, and every consumer is deferred, so document
 * order is execution order).
 */
(function () {
  'use strict';
  if (window.ddCart) return;

  var CACHE_MS    = 300;      /* simultaneous readers share one response      */
  var DEBOUNCE_MS = 250;
  var SETTLE_MS   = 1400;     /* catch a third-party write landing just after */
  var BACKOFF     = [800, 1600, 3200, 6400];   /* bounded: ~12s, then stop    */

  var cache = null, cachedAt = 0, inFlight = null, lastSig = null;
  var subs = [];
  var timer = null, settleTimer = null, backoffTimer = null, backoffAt = 0;

  /* Captured before anyone else can wrap it, so our own reads never re-enter
     another script's patch and trigger further reads. */
  var nativeFetch = window.fetch;

  function sigOf(cart) {
    if (!cart) return null;
    return (cart.items || []).map(function (i) {
      return i.variant_id + ':' + i.quantity;
    }).join('|') + '#' + cart.total_price;
  }

  function read() {
    if (inFlight) return inFlight;
    if (cache && Date.now() - cachedAt < CACHE_MS) return Promise.resolve(cache);

    inFlight = nativeFetch.call(window, '/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin'
    })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart) { cache = cart; cachedAt = Date.now(); }
        inFlight = null;
        return cache;
      })
      .catch(function () { inFlight = null; return cache; });

    return inFlight;
  }

  function notify(cart) {
    if (!cart) return;
    lastSig = sigOf(cart);
    for (var i = 0; i < subs.length; i++) {
      try { subs[i](cart); } catch (e) { /* one bad consumer must not stop the rest */ }
    }
  }

  function refresh(now) {
    clearTimeout(timer);
    timer = setTimeout(function () {
      cachedAt = 0;                      /* force a real read, not the cache */
      read().then(notify);
    }, now ? 0 : DEBOUNCE_MS);
  }

  /* A cart mutation: read now, then once more after things settle. */
  function afterMutation() {
    refresh(true);
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () { refresh(true); }, SETTLE_MS);
  }

  /*
   * The cart panel is open (or something opaque may be editing the cart).
   * A bounded, widening watch — not a fixed 1.2s drum for 45 seconds.
   */
  function poke() {
    backoffAt = 0;
    clearTimeout(backoffTimer);
    (function step() {
      if (backoffAt >= BACKOFF.length) return;
      var wait = BACKOFF[backoffAt++];
      backoffTimer = setTimeout(function () {
        if (document.visibilityState === 'visible') refresh(true);
        step();
      }, wait);
    })();
  }

  /* ---------------- the single mutation detector ---------------- */

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var out = nativeFetch.apply(window, arguments);
      if (CART_MUTATION_RE.test(String(url).split('#')[0]) && out && typeof out.then === 'function') {
        out.then(function () { afterMutation(); }, function () {});
      }
      return out;
    };
    window.fetch.__ddCartStatePatched = true;
  }

  var NativeXHR = window.XMLHttpRequest;
  if (typeof NativeXHR === 'function') {
    var open = NativeXHR.prototype.open;
    var send = NativeXHR.prototype.send;
    NativeXHR.prototype.open = function (method, url) {
      this.__ddCartUrl = url;
      return open.apply(this, arguments);
    };
    NativeXHR.prototype.send = function () {
      if (CART_MUTATION_RE.test(String(this.__ddCartUrl || '').split('#')[0])) {
        this.addEventListener('loadend', function () { afterMutation(); });
      }
      return send.apply(this, arguments);
    };
  }

  /* Theme + app signals, all funnelled into the one reader. */
  ['cart:updated', 'cart:refresh', 'cart:change', 'cart:added', 'dd:cart-added']
    .forEach(function (ev) { document.addEventListener(ev, function () { refresh(); }); });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refresh(true);
  });
  window.addEventListener('pageshow', function () { refresh(true); });

  var CART_TRIGGER_SEL = '#cart-icon-bubble, .header__icon--cart, .dd-hd-cart, a[href="/cart"], a[href$="/cart"], cart-drawer, .cart-drawer-toggle';
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest(CART_TRIGGER_SEL)) {
      refresh(true);
      poke();
    }
  }, true);

  window.ddCart = {
    get: function () { return read(); },
    peek: function () { return cache; },
    signature: function () { return lastSig; },
    refresh: refresh,
    poke: poke,
    invalidate: function () { cachedAt = 0; },
    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      subs.push(fn);
      if (cache) { try { fn(cache); } catch (e) {} }
      return function () {
        var i = subs.indexOf(fn);
        if (i !== -1) subs.splice(i, 1);
      };
    }
  };

  refresh(true);
}());
